import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';

import useGlobalStore from '../store/useGlobalStore';
import { appointmentKeys } from '../services/business/appointment/appointment.hooks';

export const SocketContext = createContext(null);

const SOCKET_PATH = '/socket.io';
const APPOINTMENT_EVENT = 'appointment_requests';
const JOIN_SHOP_EVENT = 'join_shop';
const APPOINTMENT_REQUEST_TYPE = 'APPOINTMENT_REQUEST';

function getCurrentShopId() {
    try {
        return JSON.parse(localStorage.getItem('apex-saas_details'))?._id || null;
    } catch {
        return null;
    }
}

function getPayloadShopId(payload) {
    return payload?.shopId || payload?.shop_id || payload?.shop?._id || payload?.shop?._id || null;
}

function getPayloadRequestId(payload) {
    return payload?.requestId || payload?._id || payload?.id || payload?.appointmentRequestId || null;
}

export const SocketProvider = ({ children }) => {
    const loggedIn = useSelector((state) => state?.auth?.login);
    const setTotalReqCount = useGlobalStore((s) => s.setTotalReqCount);
    const queryClient = useQueryClient();

    const [socket, setSocket] = useState(null);
    const seenIdsRef = useRef(new Set());

    const shopId = useMemo(() => getCurrentShopId(), [loggedIn]);

    useEffect(() => {
        if (!loggedIn) return;
        if (!shopId) return;
        if (!process.env.REACT_APP_SOCKET_ENDPOINT) return;

        const socketInstance = io(process.env.REACT_APP_SOCKET_ENDPOINT, {
            path: SOCKET_PATH,
            // Match Socket.IO default behavior (polling first, then upgrade to websocket)
            transports: ['polling', 'websocket'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 5000,
        });

        const logPrefix = '[socket]';
        const handleConnect = () => {
            // eslint-disable-next-line no-console
            console.log(`${logPrefix} connected`, { id: socketInstance.id, shopId, endpoint: process.env.REACT_APP_SOCKET_ENDPOINT });

            // Backend test page emits a plain string shopId
            socketInstance.emit(JOIN_SHOP_EVENT, shopId);
            // eslint-disable-next-line no-console
            console.log(`${logPrefix} emitted ${JOIN_SHOP_EVENT}`, { shopId });
        };
        const handleDisconnect = (reason) => {
            // eslint-disable-next-line no-console
            console.log(`${logPrefix} disconnected`, { reason, shopId });
        };
        const handleConnectError = (err) => {
            // eslint-disable-next-line no-console
            console.log(`${logPrefix} connect_error`, {
                message: err?.message,
                description: err?.description,
                context: err?.context,
                transport: socketInstance.io?.engine?.transport?.name,
                endpoint: process.env.REACT_APP_SOCKET_ENDPOINT,
                path: SOCKET_PATH,
                shopId,
            });
        };
        const handleReconnectError = (err) => {
            // eslint-disable-next-line no-console
            console.log(`${logPrefix} reconnect_error`, {
                message: err?.message,
                transport: socketInstance.io?.engine?.transport?.name,
                shopId,
            });
        };
        const handleReconnectAttempt = (attempt) => {
            // eslint-disable-next-line no-console
            console.log(`${logPrefix} reconnect_attempt`, { attempt, shopId });
        };

        const handleAppointmentRequest = (payload) => {
            const payloadShopId = getPayloadShopId(payload);
            if (payloadShopId && payloadShopId !== shopId) return;

            const requestId = getPayloadRequestId(payload);
            if (requestId) {
                if (seenIdsRef.current.has(requestId)) return;
                seenIdsRef.current.add(requestId);
                if (seenIdsRef.current.size > 500) {
                    const first = seenIdsRef.current.values().next().value;
                    if (first) seenIdsRef.current.delete(first);
                }
            }

            if (payload?.type !== APPOINTMENT_REQUEST_TYPE) return;
            if (typeof payload?.count !== 'number') return;
            console.log('🚀 ~ handleAppointmentRequest ~ payload:', payload);
            setTotalReqCount(payload.count);

            // Keep Outstanding Requests list + statistics in sync
            queryClient.invalidateQueries({ queryKey: [...appointmentKeys.all, 'requests'], refetchType: 'active' });
            queryClient.invalidateQueries({ queryKey: appointmentKeys.requestsCard(), refetchType: 'active' });
        };

        socketInstance.on('connect', handleConnect);
        socketInstance.on('disconnect', handleDisconnect);
        socketInstance.on('connect_error', handleConnectError);
        socketInstance.io.on('reconnect_error', handleReconnectError);
        socketInstance.io.on('reconnect_attempt', handleReconnectAttempt);
        socketInstance.on(APPOINTMENT_EVENT, handleAppointmentRequest);

        setSocket(socketInstance);

        return () => {
            socketInstance.off(APPOINTMENT_EVENT, handleAppointmentRequest);
            socketInstance.off('connect', handleConnect);
            socketInstance.off('disconnect', handleDisconnect);
            socketInstance.off('connect_error', handleConnectError);
            socketInstance.io.off('reconnect_error', handleReconnectError);
            socketInstance.io.off('reconnect_attempt', handleReconnectAttempt);
            socketInstance.disconnect();
            setSocket(null);
            seenIdsRef.current.clear();
        };
    }, [loggedIn, shopId, setTotalReqCount]);

    return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};

export const useSocket = () => useContext(SocketContext);
